import React, { Component } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { flowRight as compose, isNil } from 'lodash';
import { connect, ConnectedProps } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import memoizeOne from 'memoize-one';
import { ELSCommonUIConstants, ELSInlineLoader, ELSLoggingService, ELSTokenHelper, ELSURLHelper } from '@els/els-ui-common-react';
import { Dropdown } from '@els/els-react--dropdown';
import { ELSFillTheBlank, ELSGradeCard, ELSMultipleAnswer, ELSSingleBestAnswer } from '@els/els-component-assessment-react';
import { ELSPropsFromModalService } from '@els/els-component-modal-react';
import { ELSIcon } from '@els/els-component-form-field-react';
import { DropDownOption, FlyoutPlacements } from '../../models/ui.model';
import withHTMLHeadSEO from '../../hocs/with-html-head-seo/withHTMLHeadSEO.hoc';
import withPageLoader from '../../hocs/with-page-loader/withPageLoader.hoc';
import { studySelectors } from '../../redux/student-study/studentStudy.selectors';
import { locationActions } from '../../redux/location/location.actions';
import { studyActions } from '../../redux/student-study/studentStudy.actions';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import {
  fetchEaqAssessment,
  fetchEaqAssignment,
  fetchStudentTopicPerformances
} from '../../apis/eaq-app-facade-service/eaq-app-facade-service.utilities';
import * as constants from '../eaq-student-study/eaq-student-study.constants';
import {
  AssessmentDto,
  AssessmentPerformanceDetailsDto,
  AssessmentTypeDto,
  AssignmentDto,
  AssignmentGradeType,
  QuestionSubmissionDto
} from '../../apis/eols-assessment-service/eols-assessment-service.dtos';
import { ELSTab, ELSTabGroup, ELSTextReference } from '../../components/els.components';
import { LANGUAGE_KEY } from '../../translations/message.constants';
import { fetchAssessmentByAssignmentIdAndUserId } from '../../apis/eols-assessment-service/eols-assessment-service.utilities';
import {
  flattenedTopicReportTopics,
  getAssessmentScore,
  getFormattedAssessmentGoal,
  getFormattedAssessmentType,
  getStudentPerformance,
  renderFormattedDate,
  returnNullIfNotGraded,
  roundByDecimal
} from '../../utilities/eaq.utilities';
import { OrderQuestionDisplay } from '../../components/order-question-display/OrderQuestionDisplay.component';
import { QuestionType } from '../../constants/assessment.constants';
import IconWithText from '../../components/icon-with-text/IconWithText.component';
import { getParentLinkId } from '../../utilities/app.utilities';
import { createSupportTicketDto, HelpLink } from '../../utilities/help.utilities';
import { RightNowApplication } from '../../apis/eols-rightnow-integration-api/eols-rightnow-integration-api.dtos';
import { ELSTokenUser } from '../../models/els.dtos';
import { Messages } from '../../translations/message.models';
import StudentStudyHeaderNavComponent from '../../components/student-study-header-nav/StudentStudyHeaderNav.component';
import BackToTopButton from '../../components/back-to-top-button/BackToTopButton.component';
import SecondaryTitleHeader from '../../components/secondary-title-header/SecondaryTitleHeader.component';
import { AnalyticsAction } from '../../models/analytics.models';
import { openSupportTicket } from '../../utilities/common.utilities';
import { EaqStudentPerformanceDto, EaqStudentTopicPerformanceDto } from '../../apis/eaq-app-facade-service/eaq-app-facade-service.dtos';
import { TopicWithLineage } from '../eaq-student-topic-report/eaq-student-topic-report.models';
import { AssessmentTopicTableRow } from './eaq-student-performance-details.models';
import AssessmentTopicTableComponent from './assessment-topic-table/AssessmentTopicTable.component';
import { Application } from '../../apis/eols-app-link/eols-app-link.constants';
import StudentStudyBanner from '../../components/banner/StudentStudyBanner.component';
import { MiniEmdash } from '../../constants/studentStudy.constants';
import { RoutePath } from '../../components/app/app.constants';
import { FEATURE_FLAG } from '../../apis/eols-features-api/eols-features-api.constants';
import { isFeatureFlagEnabled } from '../../utilities/featureFlag.utilities';
import { CourseSectionDto, EntitlementDto } from '../../apis/eols-course-crud/eols-course-crud.dtos';
import { EvolveProductDto, EvolveProductTypeKey } from '../../apis/sherpath-app-facade-service/sherpath-app-facade-service.dtos';
import { MathJaxContent } from '../../components/math-jax-content/MathJaxContent.component';

const mapStateToProps = state => ({
  messages: studySelectors.getMessages(state),
  assessments: studySelectors.getStudentStudyAssessments(state),
  appLinkData: studySelectors.getLinkData(state),
  appLinkCookies: studySelectors.getAppLinkCookies(state),
  courseSectionId: studySelectors.getCourseSectionId(state),
  course: studySelectors.getCourse(state),
  evolveUser: studySelectors.getEvolveUser(state),
  featureFlagsGrouped: studySelectors.getFeatureFlagsGrouped(state),
  showPageLoader: studySelectors.getIsLoading(state),
});

const mapDispatchToProps = {
  redirect: locationActions.redirect,
  navigateToApp: studyActions.navigateToApp,
  returnAppLink: studyActions.returnAppLink,
  trackAction: studyActions.trackAction,
  fetchStudentPerformanceDetails: studyActions.fetchStudentPerformanceDetailsAction,
  openVitalSource: studyActions.openVitalSource
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export type EaqStudentPerformanceDetailsProps = PropsFromRedux & RouteComponentProps & ELSPropsFromModalService & { intl: IntlShape };

const parseEvolveProduct = (data: string): EvolveProductDto | null => {
  if (!data) {
    return null;
  }

  try {
    return JSON.parse(data) as EvolveProductDto;
  } catch (e) {
    return null;
  }
};

const getEvolveProductFromEntitlement = (entitlement: EntitlementDto): EvolveProductDto | null => {
  if (!entitlement || !entitlement.data) {
    return null;
  }

  return parseEvolveProduct(entitlement.data);
};

const getVitalSourceIsbn = (evolveProduct: EvolveProductDto | null): string | null => {
  return evolveProduct ? evolveProduct.vbId || evolveProduct.vbID || evolveProduct.isbn : null;
};

const getVitalSourceEbookComponentIsbn = (evolveProduct: EvolveProductDto): string | null => {
  const ebookProduct = evolveProduct.components && evolveProduct.components.find((component) => {
    return component.productTypeKey === EvolveProductTypeKey.SHERPATH_EBOOK_COMPONENT_NSS;
  });

  return getVitalSourceIsbn(ebookProduct || null);
};

const getVitalSourceIsbnFromBookProduct = (evolveProduct: EvolveProductDto | null): string | null => {
  if (!evolveProduct) {
    return null;
  }

  return evolveProduct.vbId
    || evolveProduct.vbID
    || getVitalSourceEbookComponentIsbn(evolveProduct)
    || evolveProduct.isbn
    || null;
};

const isVitalSourceBookEntitlement = (entitlement: EntitlementDto): boolean => {
  return Boolean(entitlement && ['EBOOK', 'COLLECTION'].includes(entitlement.type));
};

const getVitalSourceIsbnFromMatchingBookEntitlement = (
  course: CourseSectionDto,
  questionSetIsbn: string
): string | null => {
  const bookEntitlement = course.entitlements.find((entitlement) => {
    return isVitalSourceBookEntitlement(entitlement) && entitlement.isbn === questionSetIsbn;
  });

  if (!bookEntitlement) {
    return null;
  }

  return getVitalSourceIsbnFromBookProduct(getEvolveProductFromEntitlement(bookEntitlement)) || bookEntitlement.isbn;
};

const getEvolveProductComponents = (evolveProduct: EvolveProductDto): EvolveProductDto[] => {
  return evolveProduct && evolveProduct.components ? evolveProduct.components : [];
};

const getSherpathEbookIsbnFromProductComponents = (
  evolveProduct: EvolveProductDto,
  questionSetIsbn: string
): string | null => {
  const components = getEvolveProductComponents(evolveProduct);
  const sherpathQuestionSet = components.find((component) => {
    return component.isbn === questionSetIsbn
      && component.productTypeKey === EvolveProductTypeKey.SHERPATH_COMPONENT_NSS;
  });

  if (!sherpathQuestionSet) {
    return null;
  }

  const sherpathEbook = components.find((component) => {
    return component.productTypeKey === EvolveProductTypeKey.SHERPATH_EBOOK_COMPONENT_NSS;
  });

  return getVitalSourceIsbn(sherpathEbook);
};

const getSherpathEbookIsbnFromDirectProducts = (
  evolveProducts: EvolveProductDto[],
  questionSetIsbn: string
): string | null => {
  const sherpathQuestionSet = evolveProducts.find((evolveProduct) => {
    return evolveProduct.isbn === questionSetIsbn
      && evolveProduct.productTypeKey === EvolveProductTypeKey.SHERPATH_COMPONENT_NSS;
  });

  if (!sherpathQuestionSet) {
    return null;
  }

  const sherpathEbook = evolveProducts.find((evolveProduct) => {
    return evolveProduct.productTypeKey === EvolveProductTypeKey.SHERPATH_EBOOK_COMPONENT_NSS;
  });

  return getVitalSourceIsbn(sherpathEbook);
};

export const getSherpathEbookIsbnForQuestionSet = (
  course: CourseSectionDto,
  questionSetIsbn: string
): string | null => {
  if (!course || !course.entitlements || !questionSetIsbn) {
    return null;
  }

  const entitlementVitalSourceIsbn = getVitalSourceIsbnFromMatchingBookEntitlement(course, questionSetIsbn);
  if (entitlementVitalSourceIsbn) {
    return entitlementVitalSourceIsbn;
  }

  const evolveProducts = course.entitlements.reduce((products: EvolveProductDto[], entitlement) => {
    const evolveProduct = getEvolveProductFromEntitlement(entitlement);
    if (evolveProduct) {
      products.push(evolveProduct);
    }

    return products;
  }, []);

  const collectionEbookIsbn = evolveProducts.reduce((ebookIsbn: string | null, evolveProduct) => {
    return ebookIsbn || getSherpathEbookIsbnFromProductComponents(evolveProduct, questionSetIsbn);
  }, null);

  return collectionEbookIsbn || getSherpathEbookIsbnFromDirectProducts(evolveProducts, questionSetIsbn);
};

interface EaqStudentPerformanceDetailsState {
  assessmentId: number;
  assessmentType: AssessmentTypeDto;
  isbn: string;
  assessmentGoal: string;
  assessmentResults: AssessmentPerformanceDetailsDto | null;
  studentName: string;
  activeTabIndex: number;
  activeDropdownOption: DropDownOption;
  dropdownOptions: DropDownOption[];
  assignmentGradeType: string;
  assessmentStartDate: string;
  assessmentCompleteDate: string;
  assignmentDueDate: string;
  filteredQuestions: Map<number, QuestionSubmissionDto>;
  assessmentTopicRows: AssessmentTopicTableRow[];
  showInlineLoader: boolean;
  showQuestionFilter: boolean;
}

export class EaqStudentPerformanceDetails extends Component<EaqStudentPerformanceDetailsProps, EaqStudentPerformanceDetailsState, ELSPropsFromModalService> {
  getLinkablePageReferenceBookIsbn = memoizeOne((
    isLinkablePageReferenceEnabled: boolean,
    course: CourseSectionDto,
    questionSetIsbn: string
  ): string | null => {
    if (!isLinkablePageReferenceEnabled) {
      return null;
    }

    return getSherpathEbookIsbnForQuestionSet(course, questionSetIsbn);
  });

  constructor(props: EaqStudentPerformanceDetailsProps) {
    super(props);
    this.state = {
      assessmentId: null,
      assessmentType: null,
      isbn: null,
      assessmentGoal: '',
      assessmentResults: null,
      studentName: '',
      activeTabIndex: 0,
      dropdownOptions: [
        { id: 'all', label: 'all' },
        { id: 'incorrect', label: 'incorrect' },
        { id: 'correct', label: 'correct' }
      ],
      activeDropdownOption: { id: 'all', label: 'all' },
      assignmentGradeType: '',
      assessmentStartDate: '',
      assessmentCompleteDate: '',
      assignmentDueDate: '',
      filteredQuestions: new Map<number, QuestionSubmissionDto>(),
      assessmentTopicRows: [],
      showInlineLoader: false,
      showQuestionFilter: false
    };
  }

  componentDidMount() {
    this.handleLaunch();

    const eolsUser = ELSTokenHelper.getUser();
    this.setState({
      studentName: `${eolsUser.firstName} ${eolsUser.lastName}`,
    });

    const { assessmentResults } = this.state;
    if (assessmentResults && assessmentResults.questions) {
      this.setState({
        filteredQuestions: this.createQuestionResultMap(assessmentResults),
      });
    }
  }

  componentDidUpdate(prevProps: Readonly<EaqStudentPerformanceDetailsProps>, prevState: Readonly<EaqStudentPerformanceDetailsState>) {
    const isAutoCompleted = !prevState.assessmentResults && this.isAutoCompletedAssessment();
    if ((this.state.activeTabIndex === 1 && prevState.activeTabIndex !== 1) || isAutoCompleted) {
      this.getAssessmentTopicTable();
    }
  }

  handleTabChange = (index: number) => {
    this.setState({
      activeTabIndex: index
    });
  };

  isAutoCompletedAssessment = (): boolean => {
    const { assessmentResults } = this.state;
    return assessmentResults && assessmentResults.totalAnswered === 0;
  }

  handleReturnNavigation = () => {
    const { appLinkData, appLinkCookies } = this.props;
    const isInstructor = ELSTokenHelper.getUserRole() === ELSCommonUIConstants.userType.Instructor;
    const returnLinkFromURL = ELSURLHelper.getParameterByName('parentLinkId', location.search);

    if (appLinkData.targetApp === Application.NHE_ASSESSMENT_PLAYER
      && appLinkData.outPostBody.ref === RoutePath.EAQ_STUDENT_TOPIC_REPORT) {
      return this.props.redirect(RoutePath.EAQ_STUDENT_TOPIC_REPORT);
    }

    const data = appLinkData.targetApp === Application.NHE_ASSESSMENT_PLAYER && !appLinkData.outPostBody.parentLinkId && returnLinkFromURL
      ? appLinkData.outPostBody = { ...appLinkData.outPostBody, parentLinkId: returnLinkFromURL } : appLinkData;
    return this.props.returnAppLink({
      linkId: isInstructor ? appLinkCookies.linkId : getParentLinkId(data, appLinkCookies),
      returnPostBody: null
    });
  };

  isFromSelfStudyProgress = () => {
    return Boolean(ELSURLHelper.getParameterByName('assessmentId', location.search));
  };

  handleLaunch = async (): Promise<void> => {
    const { appLinkData, messages, fetchStudentPerformanceDetails } = this.props;
    const isInstructor = ELSTokenHelper.getUserRole() === ELSCommonUIConstants.userType.Instructor;

    let assignment: AssignmentDto;
    let assessment: AssessmentDto;
    let assessmentId: number;

    const isInstructorLedAssignment = appLinkData && appLinkData.outPostBody && Object.keys(appLinkData.outPostBody)
      .includes('assignmentId') && !isNil(appLinkData.outPostBody.assignmentId);

    if (isInstructorLedAssignment && !isInstructor) {
      const eolsUserId = ELSTokenHelper.getUserId();
      assignment = await fetchEaqAssignment(appLinkData.outPostBody.assignmentId);
      assessment = await fetchAssessmentByAssignmentIdAndUserId(eolsUserId, assignment.id);
      assessmentId = assessment.id;
    } else if (!isInstructorLedAssignment && !isInstructor) {
      assessmentId = Number(ELSURLHelper.getParameterByName('assessmentId', location.search));
      assessment = await fetchEaqAssessment(assessmentId);
    } else if (isInstructor) {
      assessmentId = appLinkData.outPostBody.assessmentId;
      assessment = await fetchEaqAssessment(assessmentId, appLinkData.outPostBody.studentUser.id);
      assignment = await fetchEaqAssignment(appLinkData.outPostBody.assignmentId);
      const studentName = `${appLinkData.outPostBody.studentUser.firstName} ${appLinkData.outPostBody.studentUser.lastName}`;
      this.setState({ studentName });
    }

    const assessmentResults = await fetchStudentPerformanceDetails(assessmentId);
    const hasCorrectIncorrectAnswers = Boolean(assessmentResults.questions.find(question => question.correct === false)
      && assessmentResults.questions.find(question => question.correct === true));

    return this.setState({
      assessmentId,
      assessmentResults,
      isbn: assessment.isbn,
      assessmentType: assessment.type,
      assessmentGoal: getFormattedAssessmentGoal(assessment.type, assessment.assessmentGoals[0].goal, messages),
      assessmentStartDate: assessment.createdAt,
      assessmentCompleteDate: assessmentResults.completedDateTime,
      assignmentDueDate: assessmentResults.dueDateTime,
      assignmentGradeType: assignment ? assignment.assignmentGradeType : null,
      filteredQuestions: this.createQuestionResultMap(assessmentResults),
      showQuestionFilter: hasCorrectIncorrectAnswers
    });
  };

  renderTotalQuestionsText = (assessmentResults: AssessmentPerformanceDetailsDto): JSX.Element => {
    const { intl, messages } = this.props;
    const totalQuestions: number = assessmentResults ? assessmentResults.questions.length : 0;
    const correctTotal: number = assessmentResults ? assessmentResults.totalCorrect : 0;
    const incorrectTotal: number = assessmentResults ? totalQuestions - correctTotal : 0;

    const formattedMessage = intl.formatMessage(
      {
        id: LANGUAGE_KEY.BASED_ON_TOTAL_QUESTIONS,
        defaultMessage: messages.BASED_ON_TOTAL_QUESTIONS
      },
      {
        totalQuestions: <b>{totalQuestions}</b>
      }
    );

    return (
      <p data-testid="ssa-performance-details-total-questions-text">
        {formattedMessage} ({incorrectTotal} {messages.INCORRECT.toLowerCase()}, {correctTotal} {messages.CORRECT.toLowerCase()})
      </p>
    );
  };

  handleReportContentErrorClick = (questionVtwId: string, questionType: string) => {
    const { course, evolveUser } = this.props;
    const { isbn, assessmentId, assessmentType } = this.state;
    const user: ELSTokenUser = ELSTokenHelper.getUser();

    const urlParams: Record<string, string> = {
      ...(this.isFromSelfStudyProgress() ? {} : { assessmentId: String(assessmentId) }),
      questionVtwId,
      questionType,
      isbn
    };

    const supportTicket = createSupportTicketDto(
      user,
      course,
      user.role,
      evolveUser,
      RightNowApplication.EAQ,
      HelpLink.RIGHT_NOW_REPORT_A_CONTENT_ERROR,
      urlParams
    );

    this.props.trackAction({
      action: AnalyticsAction.EAQ_REPORT_CONTENT_ERROR_CLICKED,
      props: {
        assessmentId,
        assessmentType,
        questionVtwId,
        questionType,
        isbn
      }
    });

    openSupportTicket(supportTicket);
  };

  isLinkablePageReferenceEnabled = (): boolean => {
    const { courseSectionId, featureFlagsGrouped } = this.props;
    if (!featureFlagsGrouped || !courseSectionId) {
      return false;
    }

    return Boolean(
      isFeatureFlagEnabled(
        FEATURE_FLAG.ENABLE_EAQ_LINKABLE_PAGE_SHERPATH,
        featureFlagsGrouped,
        courseSectionId,
        Application.EAQ
      )
    );
  }

  formatTextReference = (textReference: string | string[], messages: Messages, questionVtwId: string, questionType: string): JSX.Element => {
    const { course, openVitalSource } = this.props;
    const linkablePageReferenceBookIsbn = this.getLinkablePageReferenceBookIsbn(
      this.isLinkablePageReferenceEnabled(),
      course,
      this.state.isbn
    );

    const isTextReferenceLinkable = Boolean(linkablePageReferenceBookIsbn);
    const handlePageLinkClick = (pageNumber: number) => {
      openVitalSource(linkablePageReferenceBookIsbn, pageNumber);
    };

    return (
      <div className="u-els-margin-top">
        <FlexLayout modifiers={[FlexLayoutModifier.MIDDLE, FlexLayoutModifier.WRAP_AT_MOBILE]}>
          <FlexItem modifiers={[FlexLayoutModifier.GROW]} classes={['u-els-padding-top@mobile']}>
            <span data-testid={`ssa-performance-details-page-reference-button-${questionVtwId}`}>
              <ELSTextReference
                textReference={textReference || ''}
                isLinkable={isTextReferenceLinkable}
                onPageLinkClick={handlePageLinkClick}
              />
            </span>
          </FlexItem>
          <FlexItem
            modifiers={[FlexLayoutModifier.RIGHT]}
            classes={['u-els-width-1o1@mobile', 'u-els-padding-top-2x@mobile']}
          >
            <button
              type="button"
              className="u-els-anchorize c-els-link"
              onClick={() => this.handleReportContentErrorClick(questionVtwId, questionType)}
              data-testid={`ssa-performance-details-report-error-button-${questionVtwId}`}
            >
              <IconWithText
                iconPrefix="gizmo"
                iconName="writing"
              >
                <p className="c-els-link__text u-els-font-size-base u-els-font-family-base">{messages.REPORT_CONTENT_ERROR}</p>
              </IconWithText>
            </button>
          </FlexItem>
        </FlexLayout>
      </div>
    );
  };

  // * - Safeguard for FIB selected answers that have comma (only for selected answers marked `correct: true`)
  // * - Example - Actual user input: 12,345 ->
  // * - returns in /results API as selectedAnswers: ["12", "345"], responseChoices: {CHOICE_0: "", CHOICE_1: "12,345"}
  // * - Protection against math content brought up in https://elsevier.atlassian.net/browse/CITS-36026
  fillInTheBlankCommaFormat = (question) => {
    const responseChoiceValues = Object.values(question.qtiData.responseChoices);

    // * - responseChoices object -> Array of response choice values, omitting the CHOICE_0's empty string
    const responseValueArr = responseChoiceValues
      .map(value => {
        if (typeof value === 'string') {
          return value;
        }

        return value[0];
      }).slice(1);

    const incorrectWithOneTextbox = !question.correct && responseChoiceValues.length === 2;

    // * - Check if FIB question has more selectedAnswers than there are correctResponses because commas will always split answers
    const isMoreAnswersThanOptions = question.selectedAnswers.length > question.qtiData.correctResponse.length;

    // * - Regex passes with whole numbers with commas from `1,000` - `9,999,999`
    const WITH_COMMA_FORMAT = /^[1-9]\d{0,2}(,\d{3}){1,2}(\.\d{1,3})?$/;

    // * - Regex passes with strings that have a `/` and `,` - ex: 56/2,300
    const WITH_FRACTION_COMMA_FORMAT = /(?=.*\/)(?=.*,)/;

    const isCommaRequired = responseValueArr.some(response => WITH_COMMA_FORMAT.test(response)
      || WITH_FRACTION_COMMA_FORMAT.test(response));

    if (isMoreAnswersThanOptions && incorrectWithOneTextbox) {
      let newSelectedAnswersArr = question.selectedAnswers;

      const concatenatedAnswerInThousands = [`${question.selectedAnswers[0]},${question.selectedAnswers[1]}`];
      const concatenatedAnswerInMillions = [`${question.selectedAnswers[0]},${question.selectedAnswers[1]},${question.selectedAnswers[2]}`];

      if (question.selectedAnswers.length === 2) {
        newSelectedAnswersArr = concatenatedAnswerInThousands;
      }

      if (question.selectedAnswers.length === 3) {
        newSelectedAnswersArr = concatenatedAnswerInMillions;
      }

      return {
        ...question,
        selectedAnswers: newSelectedAnswersArr,
      };
    }

    if (isMoreAnswersThanOptions && isCommaRequired && question.correct) {
      // * - Change responseValueArr into array of strings split up by comma
      const choiceParts = responseValueArr.reduce((acc, currentValue) => {
        const parts = currentValue.split(',');

        parts.forEach(x => acc.push(x));

        return acc;
      }, []);

      // * - Do a 1:1 check if the new responseChoices array matches the selectedAnswers array (this is just a double check)
      const isChoiceSelectedAnswerMatch = JSON.stringify(choiceParts) === JSON.stringify(question.selectedAnswers);

      return {
        ...question,
        selectedAnswers: isChoiceSelectedAnswerMatch ? responseValueArr : question.selectedAnswers
      };
    }

    return question;
  }

  displayQuestionType = (question: QuestionSubmissionDto, index: number): JSX.Element => {
    const { qtiData } = question;
    const { messages } = this.props;
    const rationaleTypeBlock = 'block';
    const rationaleBlockTitle = <b>{messages.RATIONALE}</b>;
    const questionPrompt = qtiData.prompt.replace(/<style type="text\/css"\/>/g, '');
    const textReference = qtiData.textReference
      ? this.formatTextReference(qtiData.textReference as string | string[], messages, question.qtiData.vtwId, question.qtiData.questionType) : [];

    const questionId = `${qtiData.vtwId}-number${index}`;
    switch (question.qtiData.questionType) {
      case QuestionType.MCSA:
        return (
          <ELSSingleBestAnswer
            questionPrompt={questionPrompt}
            questionChoices={qtiData.responseChoices}
            questionFeedback={qtiData.feedback}
            correctAnswers={qtiData.correctResponse}
            textReference={textReference}
            selectedAnswers={question.selectedAnswers}
            rationaleType={rationaleTypeBlock}
            rationaleBlockTitle={rationaleBlockTitle}
            isTextSelectionDisabled
            vtwId={qtiData.vtwId}
            questionId={questionId}
            hideQuestionId
            data-testid={`ssa-performance-details-question-${questionId}`}
          />
        );
      case QuestionType.MCMA:
        return (
          <ELSMultipleAnswer
            questionPrompt={questionPrompt}
            questionChoices={qtiData.responseChoices}
            questionFeedback={qtiData.feedback}
            correctAnswers={qtiData.correctResponse}
            selectedAnswers={question.selectedAnswers}
            isShowSuccessFlagAllCorrectAnswer
            rationaleType={rationaleTypeBlock}
            textReference={textReference}
            rationaleBlockTitle={rationaleBlockTitle}
            isTextSelectionDisabled
            questionId={questionId}
            hideQuestionId
            data-testid={`ssa-performance-details-question-${questionId}`}
          />
        );
      case QuestionType.ORDER:
        return (
          <OrderQuestionDisplay
            question={{
              prompt: questionPrompt,
              responseChoices: qtiData.responseChoices,
              feedback: {
                feedback: qtiData.feedback[Object.keys(qtiData.feedback)[0]],
              },
              correctResponse: qtiData.correctResponse,
              textReference,
              questionType: qtiData.questionType
            }}
            selectedAnswers={question.selectedAnswers}
            rationaleBlockTitle={rationaleBlockTitle}
            questionId={questionId}
          />
        );
      case QuestionType.FILL_IN_BLANK: {
        // * - Check if FIB question has more selectedAnswers than there are correctResponses because commas will always split answers
        const isMoreAnswersThanOptions = question.selectedAnswers.length > question.qtiData.correctResponse.length;
        let fillInBlankQuestionData = null;
        if (isMoreAnswersThanOptions) {
          fillInBlankQuestionData = this.fillInTheBlankCommaFormat(question);
        }
        return (
          <ELSFillTheBlank
            questionPrompt={questionPrompt}
            questionChoices={qtiData.responseChoices}
            questionFeedback={qtiData.feedback}
            rationaleType={rationaleTypeBlock}
            rationaleBlockTitle={rationaleBlockTitle}
            textReference={textReference}
            correctAnswers={qtiData.correctResponse}
            selectedAnswers={(fillInBlankQuestionData && fillInBlankQuestionData.selectedAnswers) || question.selectedAnswers}
            isCompleted
            isTextSelectionDisabled
            normalizeAnswers={false}
            questionId={questionId}
            hideQuestionId
            data-testid={`ssa-performance-details-question-${questionId}`}
          />
        );
      }
      default:
        return <></>;
    }
  };

  displayCorrectnessPill = (question: QuestionSubmissionDto) => {
    const { messages } = this.props;
    return (
      <span
        className={`u-els-display-inline-block c-els-pill ${
          question.correct
            ? 'u-els-color-confirm u-els-background-color-confirm-background'
            : 'u-els-color-warn u-els-background-color-warn-background'
        }`}
        data-testid={`ssa-performance-details-correctness-pill-${question.qtiData.vtwId}`}
      >
        <IconWithText
          iconName={question.correct ? 'checkmark' : 'close'}
          iconPrefix="hmds"
        >
          {question.correct ? messages.CORRECT : messages.INCORRECT}
        </IconWithText>
      </span>
    );
  };

  createQuestionResultMap = (assessmentResults: AssessmentPerformanceDetailsDto): Map<number, QuestionSubmissionDto> | null => {
    if (assessmentResults && assessmentResults.questions) {
      const questionMap = new Map<number, QuestionSubmissionDto>();
      assessmentResults.questions.forEach((question, index) => {
        questionMap.set(index + 1, question);
      });
      return questionMap;
    }
    return null;
  };

  displayQuestionsFromMap = (questionMap: Map<number, QuestionSubmissionDto>): JSX.Element => {
    if (!questionMap) {
      return <></>;
    }

    return (
      <FlexLayout>
        <FlexItem>
          <MathJaxContent>
            {Array.from(questionMap.entries()).map(([index, question]) => (
              <div
                className="c-ssa-eaq-student-performance-details__question-item"
                key={`${question.qtiData.vtwId}: ${index} - ${this.state.activeDropdownOption.id}`}
                data-testid={`ssa-performance-details-question-item-${question.qtiData.vtwId}`}>
                {this.displayCorrectnessPill(question)}
                <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS]}>
                  <FlexItem>
                    <span className="u-els-margin-top u-els-font-size-h4 u-els-margin-top-1x1o2@mobile">{index}. </span>
                  </FlexItem>
                  <FlexItem modifiers={[FlexLayoutModifier.GROW]} classes={['u-els-padding-left-1o2']}>
                    {this.displayQuestionType(question, index)}
                  </FlexItem>
                </FlexLayout>
              </div>
            ))}
          </MathJaxContent>
        </FlexItem>
      </FlexLayout>
    );
  };

  handleDropdownChange = (newOptionSelected: DropDownOption) => {
    const { assessmentResults } = this.state;

    this.setState({ activeDropdownOption: newOptionSelected, showInlineLoader: true });

    if (!assessmentResults || !assessmentResults.questions) {
      return;
    }

    const questionMap = this.createQuestionResultMap(assessmentResults);

    let filteredQuestions: [number, QuestionSubmissionDto][] = Array.from(questionMap.entries());

    if (newOptionSelected.id === 'correct') {
      filteredQuestions = filteredQuestions.filter(([, question]) => question.correct);
    } else if (newOptionSelected.id === 'incorrect') {
      filteredQuestions = filteredQuestions.filter(([, question]) => !question.correct);
    }

    this.setState({
      filteredQuestions: new Map(filteredQuestions),
    });

    setTimeout(() => this.setState({ showInlineLoader: false }), 250);
  }

  renderQuestionTabPageContent = () => {
    const { messages } = this.props;
    const {
      assessmentResults,
      dropdownOptions,
      activeDropdownOption,
      filteredQuestions,
      showInlineLoader,
      showQuestionFilter
    } = this.state;

    return (
      <div className="u-els-padding-top-1x">
        <FlexLayout
          modifiers={[FlexLayoutModifier.MIDDLE, FlexLayoutModifier.WRAP_AT_MOBILE]}
          classes={['u-els-margin-bottom-2x']}
        >
          <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
            <h3 data-testid="ssa-performance-details-quiz-heading">{messages.HOW_DID_I_PERFORM_ON_THIS_QUIZ}</h3>
            {this.renderTotalQuestionsText(assessmentResults)}
          </FlexItem>
          <FlexItem
            modifiers={[FlexLayoutModifier.RIGHT]}
            classes={['u-els-width-1o1@mobile u-els-padding-top@mobile']}
          >
            {showQuestionFilter && (
            <div role="form" aria-label={`${messages.VIEWING} ${activeDropdownOption.label} ${messages.QUESTIONS}`}>
              <FlexLayout modifiers={[FlexLayoutModifier.LEFT]}>
                <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS_1o2]}>
                  <FlexItem modifiers={[FlexLayoutModifier.RIGHT]}>
                    <p>{messages.VIEWING}</p>
                  </FlexItem>
                  <FlexItem>
                    <Dropdown
                      id="c-ssa-filter-dropdown"
                      className="c-ssa-filter-dropdown"
                      options={dropdownOptions}
                      selected={activeDropdownOption}
                      showSelectedAsLabel
                      onChange={this.handleDropdownChange}
                      flyoutProps={{
                        pointer: false,
                        pointerType: 'primary',
                        placement: 'bottom-end'
                      }}
                    />
                  </FlexItem>
                  <FlexItem modifiers={[FlexLayoutModifier.LEFT]}>
                    <p>{messages.QUESTIONS.toLowerCase()}</p>
                  </FlexItem>
                </FlexLayout>
              </FlexLayout>
            </div>
            )}
          </FlexItem>
        </FlexLayout>
        <FlexLayout>
          {showInlineLoader && <ELSInlineLoader />}
          { !showInlineLoader && (
          <FlexItem modifiers={[FlexLayoutModifier.CENTER]}>
            {this.displayQuestionsFromMap(filteredQuestions)}
          </FlexItem>
          )}
        </FlexLayout>
      </div>
    );
  }

  renderTopicTabPageContent = (): JSX.Element => {
    const { messages } = this.props;
    const {
      assessmentId,
      assessmentResults,
      assessmentTopicRows,
      showInlineLoader,
      isbn
    } = this.state;

    return (
      <>
        <FlexLayout
        modifiers={[FlexLayoutModifier.MIDDLE, FlexLayoutModifier.WRAP_AT_MOBILE]}
        classes={['u-els-margin-bottom-2x']}
      >
          <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
            <div className="u-els-padding-top-1x">
              <h3 data-testid="ssa-performance-details-topics-heading">{
                this.isMultipleTopic()
                  ? messages.WHAT_TOPICS_WERE_TESTED_ON_THIS_QUIZ
                  : messages.WHAT_TOPIC_WAS_TESTED_ON_THIS_QUIZ
              }
              </h3>
              <h4 data-testid="ssa-performance-details-topics-count-text">
                <b>{assessmentResults.topics ? assessmentResults.topics.length : 0}</b> {
                this.isMultipleTopic()
                  ? messages.TOPICS_TESTED
                  : messages.TOPIC_TESTED
              }
              </h4>
            </div>
          </FlexItem>
        </FlexLayout>
        <FlexLayout>
          <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
            {showInlineLoader && <ELSInlineLoader />}
            {!showInlineLoader
              && assessmentTopicRows
              && (
              <AssessmentTopicTableComponent
                    isMultipleTopic={this.isMultipleTopic()}
                    assessmentId={assessmentId}
                    assessmentTopicRows={assessmentTopicRows}
                    isbn={isbn}
                  />
              )}
          </FlexItem>
        </FlexLayout>
      </>
    );
  }

  getAssessmentTopicTableRows = (flattenedTopics: TopicWithLineage[]): AssessmentTopicTableRow[] => {
    if (!flattenedTopics || !flattenedTopics.length) {
      return null;
    }

    return flattenedTopics.map((topic: TopicWithLineage) => {
      const studentPerformance: EaqStudentPerformanceDto = getStudentPerformance(topic.item);
      const topicName = topic.item.displayName || topic.item.text;

      const lineageDisplay = topic.lineage
        .filter((parent: EaqStudentTopicPerformanceDto) => topicName !== parent.displayName && topicName !== parent.text)
        .map((parent: EaqStudentTopicPerformanceDto) => parent.displayName || parent.text)
        .join(' / ');

      return {
        topicName,
        topicVtwId: topic.item.vtwId,
        masteryLevel: studentPerformance.masteryLevel,
        questionsAnswered: studentPerformance.questionsAnswered,
        lineageDisplay
      };
    });
  };

  getAssessmentTopicTable = async () => {
    this.setState({ showInlineLoader: true });
    try {
      const { assessmentResults, isbn } = this.state;
      const { appLinkData } = this.props;

      const isInstructor = ELSTokenHelper.getUserRole() === ELSCommonUIConstants.userType.Instructor;
      const altStudentUserId = appLinkData && appLinkData.outPostBody && appLinkData.outPostBody.studentUser && Object.keys(appLinkData.outPostBody.studentUser).includes('id')
        ? appLinkData.outPostBody.studentUser.id : null;

      let studentTopicPerf = null;

      if (isInstructor && altStudentUserId) {
        studentTopicPerf = await fetchStudentTopicPerformances(isbn, altStudentUserId);
      } else {
        studentTopicPerf = await fetchStudentTopicPerformances(isbn);
      }
      const flattenedTopics = flattenedTopicReportTopics(studentTopicPerf, 'ALL');
      const filteredTopics = flattenedTopics.filter((topic) =>
        assessmentResults.topics
          .some((assessmentTopic) => assessmentTopic.vtwId === topic.item.vtwId));

      this.setState({ assessmentTopicRows: this.getAssessmentTopicTableRows(filteredTopics) });
    } catch (error) {
      ELSLoggingService.error(error);
    } finally {
      this.setState({ showInlineLoader: false });
    }
  }

  isMultipleTopic = () => {
    const topicsArray = this.state.assessmentResults.topics;
    return topicsArray && topicsArray.length > 1;
  }

  renderGradeCardFlyoutContent = (gradeType: string): string => {
    const { assignmentDueDate, assessmentCompleteDate } = this.state;
    const { messages } = this.props;
    const isPastDue = assignmentDueDate < assessmentCompleteDate;
    let content = null;
    switch (gradeType) {
      case AssignmentGradeType.PASS_FAIL:
        content = messages.FLYOUT_CONTENT_BY_DUE_DATE;
        break;
      case AssignmentGradeType.SCORED:
        content = isPastDue ? messages.FLYOUT_CONTENT_BY_DUE_DATE : messages.FLYOUT_CONTENT_SCORED;
        break;
      case AssignmentGradeType.NOT_GRADED:
        content = messages.FLYOUT_CONTENT_NOT_GRADED;
        break;
      default:
        break;
    }

    return content;
  }

  renderAutoCompleteBanner = (messages: Partial<Record<LANGUAGE_KEY, string>>) => {
    return (
      <StudentStudyBanner
      isVisible={this.isAutoCompletedAssessment()}
      className="c-ssa-eaq-student-performance-details__banner"
    >
        <>
          <FlexLayout modifiers={[FlexLayoutModifier.LEFT]}>
            <FlexItem classes={['u-els-margin-right-1o2']}>
              <ELSIcon name="standard" prefix="hmds" size="1x1o2" color="confirm" />
            </FlexItem>
            <FlexItem>
              <p data-testid="ssa-performance-details-autocomplete-banner-text">
                <b>{messages.CONGRATS_AUTOCOMPLETE}</b>
                <br />
                <span className="c-ssa-eaq-student-performance-details__did-you-know">
                  {messages.DID_YOU_KNOW_BOOST_MASTERY}
                </span>
              </p>
            </FlexItem>
          </FlexLayout>
        </>
      </StudentStudyBanner>
    );
  }
  renderPerformanceDetailsBanner = (
    messages: Partial<Record<LANGUAGE_KEY, string>>,
    studentName: string,
    assessmentType: AssessmentTypeDto,
    assessmentGoal: string,
    assessmentStartDate: string,
    isMasteryType: boolean,
    assignmentDueDate: string,
    assessmentCompleteDate: string,
    gradeType: string,
    assessmentResults: AssessmentPerformanceDetailsDto
  ) => {
    return (
      <FlexLayout modifiers={[FlexLayoutModifier.MIDDLE, FlexLayoutModifier.WRAP_AT_MOBILE]} classes={['c-els-screen-max-width']}>
        <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
          <FlexLayout modifiers={[FlexLayoutModifier.LEFT]}>
            <FlexItem classes={['u-els-margin-bottom-1x1o2']}>
              <strong>
                <h1 className="u-els-font-size-h2" data-testid="ssa-performance-details-quiz-name">
                  {assessmentResults.quizName ? assessmentResults.quizName : assessmentResults.topics[0].text}
                </h1>
              </strong>
            </FlexItem>
          </FlexLayout>
          <FlexLayout modifiers={[
            FlexLayoutModifier.LEFT,
            FlexLayoutModifier.GUTTERS_2X,
            FlexLayoutModifier.WRAP_AT_MOBILE,
            FlexLayoutModifier.WRAP_AT_TABLET
          ]}>
            <FlexItem
            classes={isMasteryType
              ? ['u-els-padding-left-3x@mobile u-els-padding-left-3x@tablet']
              : ['u-els-padding-left-2x@mobile u-els-padding-left-2x@tablet']}
          >
              <table role="presentation">
                <tbody>
                  <tr>
                    <td className="u-els-float-right u-els-padding-right-1o2">
                      <h2 className="u-els-font-size-h4">{messages.STUDENT}:</h2>
                    </td>
                    <td>
                      <h2 className="u-els-font-size-h4">{studentName}</h2>
                    </td>
                  </tr>
                  <tr>
                    <td className="u-els-float-right u-els-padding-right-1o2">
                      <h2 className="u-els-font-size-h4">{messages.TYPE}:</h2>
                    </td>
                    <td>
                      <h2 className="u-els-font-size-h4">{getFormattedAssessmentType(assessmentType, messages)}</h2>
                    </td>
                  </tr>
                  <tr>
                    <td className="u-els-float-right u-els-padding-right-1o2">
                      <h2 className="u-els-font-size-h4">{isMasteryType ? messages.GOAL : messages.QUESTIONS}:</h2>
                    </td>
                    <td>
                      <h2 className="u-els-font-size-h4">{assessmentGoal}</h2>
                    </td>
                  </tr>
                </tbody>
              </table>
            </FlexItem>
            <FlexItem>
              <table role="presentation">
                <tbody>
                  <tr>
                    <td className="u-els-float-right u-els-padding-right-1o2">
                      <h2 className="u-els-font-size-h4">{messages.DUE}:</h2>
                    </td>
                    <td>
                      <h2 className="u-els-font-size-h4">{renderFormattedDate(assessmentResults.dueDateTime)}</h2>
                    </td>
                  </tr>
                  <tr>
                    <td className="u-els-float-right u-els-padding-right-1o2">
                      <h2 className="u-els-font-size-h4">{messages.START}:</h2>
                    </td>
                    <td>
                      <h2 className="u-els-font-size-h4">{renderFormattedDate(assessmentStartDate)}</h2>
                    </td>
                  </tr>
                  <tr>
                    <td className="u-els-float-right u-els-padding-right-1o2">
                      <h2 className="u-els-font-size-h4">{messages.COMPLETE}:</h2>
                    </td>
                    <td>
                      <h2 className="u-els-font-size-h4">{renderFormattedDate(assessmentCompleteDate)}</h2>
                    </td>
                  </tr>
                </tbody>
              </table>
            </FlexItem>
          </FlexLayout>
        </FlexItem>
        <FlexItem modifiers={[FlexLayoutModifier.LEFT]} classes={['u-els-padding-top@mobile']}>
          <FlexLayout modifiers={[FlexLayoutModifier.MIDDLE]}>
            <FlexItem>
              <ELSGradeCard
              data-testid="ssa-performance-details-grade-card"
              gradeType={gradeType}
              scorePercentage={returnNullIfNotGraded(gradeType, getAssessmentScore(assessmentResults))}
              earnedGradePoints={returnNullIfNotGraded(gradeType, roundByDecimal((assessmentResults.percentage * assessmentResults.gradePoints), 2))}
              totalGradePoints={returnNullIfNotGraded(gradeType, assessmentResults.gradePoints)}
              isPastDue={assignmentDueDate && assignmentDueDate < assessmentCompleteDate}
              correctAnswered={assessmentResults.totalAnswered > 0 ? `${getAssessmentScore(assessmentResults)}%` : MiniEmdash}
              flyoutContent={this.renderGradeCardFlyoutContent(gradeType)}
              flyoutWidth="265px"
              flyoutPosition={FlyoutPlacements.BOTTOM_END}
            />
            </FlexItem>
          </FlexLayout>
        </FlexItem>
      </FlexLayout>
    );
  }

  render() {
    const { messages } = this.props;
    const {
      assessmentResults,
      studentName,
      activeTabIndex,
      assessmentType,
      assessmentGoal,
      assessmentStartDate,
      assessmentCompleteDate,
      assignmentDueDate,
      assignmentGradeType
    } = this.state;
    const isMasteryType = assessmentType && assessmentType === AssessmentTypeDto.MASTERY;
    const isCbqType = assessmentType && assessmentType === AssessmentTypeDto.QUIZ_BY_QUESTION;
    const gradeType = assignmentGradeType || AssignmentGradeType.NOT_GRADED;

    return (
      <div className="c-ssa-eaq-student-performance-details" role="main" data-testid="ssa-performance-details-page">
        <StudentStudyHeaderNavComponent
          handleReturnNavigation={this.handleReturnNavigation}
          headerTitle={messages.ELSEVIER_ADAPTIVE_QUIZZING}
          isMenuItemsEnabled={false}
          headerTitleClassName="u-els-color-secondary"
          isSticky
        />
        <BackToTopButton
          buttonText={messages.BACK_TO_TOP}
        />
        {assessmentResults?.topics && (
          <div className="c-ssa-student-performance-details">
            <SecondaryTitleHeader
              title={assessmentResults.quizName ? assessmentResults.quizName : assessmentResults.topics[0].text}
            />
            {this.renderAutoCompleteBanner(messages)}
            <div className="c-ssa-eaq-student-performance-details__description-block">
              {this.renderPerformanceDetailsBanner(
                messages,
                studentName,
                assessmentType,
                assessmentGoal,
                assessmentStartDate,
                isMasteryType,
                assignmentDueDate,
                assessmentCompleteDate,
                gradeType,
                assessmentResults
              )}
            </div>
          </div>
        )}
        <div className="c-ssa-eaq-student-performance-details__page-content c-els-screen-max-width">
          <FlexLayout modifiers={[FlexLayoutModifier.MIDDLE]}>
            <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
              <div className="c-ssa-eaq-student-performance-details__tab-group">
                {isCbqType && this.renderQuestionTabPageContent()}
                {isMasteryType && this.isAutoCompletedAssessment() && this.renderTopicTabPageContent()}
                {!isCbqType && !this.isAutoCompletedAssessment() && (
                  <ELSTabGroup
                    onChangeActiveIndex={this.handleTabChange}
                    activeIndex={activeTabIndex}
                  >
                    <ELSTab title={messages.QUESTIONS}>
                      {this.renderQuestionTabPageContent()}
                    </ELSTab>

                    {assessmentResults && (
                      <ELSTab title={this.isMultipleTopic() ? messages.MULTIPLE_TOPICS : messages.TOPIC}>
                        {this.renderTopicTabPageContent()}
                      </ELSTab>
                    )}
                  </ELSTabGroup>
                )}
              </div>
            </FlexItem>
          </FlexLayout>
        </div>
      </div>
    );
  }
}

const enhancers = [
  withHTMLHeadSEO({ title: constants.eaqStudentStudyPerformanceDetailsPageTitle }),
  injectIntl,
  connector,
  withPageLoader
];

export default compose(...enhancers)(EaqStudentPerformanceDetails);
